import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebaseConfig";

import "../styles/generalStyle.css";
import "./SongDetails.css";
import { useLanguage } from "../i18n/LanguageContext";
import ShareSongButton from "../components/ShareSongButton";



export default function SongDetailsPage() {
  const { songId } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();

  const [firebaseUser, setFirebaseUser] = useState(() => auth.currentUser);

  const [editandoTitulo, setEditandoTitulo] = useState(false);
  const [nuevoTitulo, setNuevoTitulo] = useState("");
  const [savingTitle, setSavingTitle] = useState(false);
  const [msgTitle, setMsgTitle] = useState("");


  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [song, setSong] = useState(null);

  // precio (string) desde precioCancion/precioWeb -> usd
  const [priceUsd, setPriceUsd] = useState(null);

  // player
  const audioRef = useRef(null);
  const [playingKey, setPlayingKey] = useState(null); // "v1" | "v2"
  const [isPlaying, setIsPlaying] = useState(false);

  // avisos de acciones bloqueadas
  const [needPayMsg, setNeedPayMsg] = useState("");

  const defaultCurrency = useMemo(() => {
      // de/fr -> EUR, lo demás -> USD
      return ["de", "fr"].includes(lang) ? "EUR" : "USD";
    }, [lang]);
  
  const prices = { USD: 15, EUR: 15, BRL: 60 }; 
  const amount = prices[defaultCurrency] ?? 15;

  const priceLabel = useMemo(() => {
    const locale = lang || "en";
    const currency = defaultCurrency;

    try {
      // 1) número con separadores del idioma (es: 15,000 | en: 15,000)
      const number = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);

      // 2) símbolo: si es USD, fuerza en-US para que sea "$" y no "US$"
      const symbolLocale = currency === "USD" ? "en-US" : locale;

      const symbol = new Intl.NumberFormat(symbolLocale, {
        style: "currency",
        currency,
        currencyDisplay: "narrowSymbol",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
        .formatToParts(0)
        .find((p) => p.type === "currency")?.value || currency;

      return `${symbol}${number} ${currency}`; // ✅ "$15 USD"
    } catch {
      return `${currency} ${amount}`;
    }
  }, [amount, defaultCurrency, lang]);


  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setFirebaseUser(u || null));
    return () => unsub();
  }, []);

  const ensureAudio = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.preload = "none";
      audioRef.current.addEventListener("ended", () => {
        setIsPlaying(false);
        setPlayingKey(null);
      });
      audioRef.current.addEventListener("pause", () => setIsPlaying(false));
      audioRef.current.addEventListener("play", () => setIsPlaying(true));
    }
    return audioRef.current;
  };

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
        audioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!firebaseUser || !songId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");
        setMsgTitle("");


        // 1) canción
        const songRef = doc(db, "usuarios", firebaseUser.uid, "canciones", songId);
        const snap = await getDoc(songRef);
        if (!snap.exists()) {
          setError(t("songDetails.notFound"));
          setLoading(false);
          return;
        }
        const data = snap.data();       
        const songLoaded = { id: snap.id, ...data };
        setSong(songLoaded);

        setNuevoTitulo((songLoaded.titulo || "").toString());

        // 2) precio
        const priceRef = doc(db, "precioCancion", "precioWeb");
        const priceSnap = await getDoc(priceRef);
        if (priceSnap.exists()) {
          const p = priceSnap.data();
          setPriceUsd(typeof p.usd === "string" ? p.usd : null);
        } else {
          setPriceUsd(null);
        }
      } catch (e) {
        console.error("Error cargando canción:", e);
        setError(t("songDetails.errorLoad"));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [firebaseUser, songId, t]);

  const paid = useMemo(() => !!song?.pagoWeb, [song]);
  const v1Url = useMemo(() => (song?.coverUrl || "").trim(), [song]);
  const v2Url = useMemo(() => (song?.coverUrl02 || "").trim(), [song]);

const tituloMostrado = useMemo(() => {
  const raw = (song?.titulo || "").toString().trim();
  return raw || t("song.noTitle"); // "Sin título" / "Untitled"
}, [song?.titulo, t]);

const onClickEditarTitulo = () => {
  setMsgTitle("");
  setNuevoTitulo((song?.titulo || "").toString());
  setEditandoTitulo(true);
};

const onCancelarEditarTitulo = () => {
  setMsgTitle("");
  setNuevoTitulo((song?.titulo || "").toString());
  setEditandoTitulo(false);
};

const onGuardarNuevoTitulo = async () => {
  if (!firebaseUser?.uid || !songId) return;

  try {
    setSavingTitle(true);
    setMsgTitle("");

    const tituloClean = (nuevoTitulo || "").toString().trim();

    // ✅ 1) actualizar doc principal
    const songRef = doc(db, "usuarios", firebaseUser.uid, "canciones", songId);
    await setDoc(
      songRef,
      {
        titulo: tituloClean, // si lo borran queda "" y tu UI muestra t("song.noTitle")
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    // ✅ 2) (opcional recomendado) actualizar mini doc para compartir
    const webRef = doc(db, "cancionesWeb", songId);
    await setDoc(
      webRef,
      {
        titulo: tituloClean || null,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    // ✅ refrescar UI local
    setSong((prev) => ({ ...(prev || {}), titulo: tituloClean }));

    setEditandoTitulo(false);
    setMsgTitle(t("common.savedOk")); // "Guardado ✅"
  } catch (e) {
      console.error("Error guardando título:", e);
      setMsgTitle(t("common.saveError"));
  } finally {
      setSavingTitle(false);
  }
};


  const statusLabel = (s) => {
    if (!s) return "—";
    const v = String(s).toLowerCase();
    if (v === "pending") return t("songDetails.statusPending");
    if (v === "done") return t("songDetails.statusDone");
    if (v === "error") return t("songDetails.statusError");
    return s;
  };

  const formatDate = (value) => {
    if (!value) return "—";
    try {
      const d =
        typeof value.toDate === "function"
          ? value.toDate()
          : value instanceof Date
          ? value
          : new Date(value);
      return d.toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return "—";
    }
  };

  const showNeedPay = () => {
    setNeedPayMsg(t("songDetails.needPay"));
    setTimeout(() => setNeedPayMsg(""), 1600);
  };

  const handleTogglePlay = async (version, url) => {
    if (!url) return;

    const audio = ensureAudio();
    try {
      if (playingKey === version && !audio.paused) {
        audio.pause();
        return;
      }
      setPlayingKey(version);
      audio.pause();
      audio.src = url;
      await audio.play();
    } catch (e) {
      console.error("play error:", e);
      setError(t("songDetails.playError"));
      setIsPlaying(false);
      setPlayingKey(null);
    }
  };

  const handleCopyUrl = async (url) => {
    if (!paid) return showNeedPay();
    if (!url) return;

    try {
      await navigator.clipboard.writeText(url);
      setNeedPayMsg(t("songDetails.copied"));
      setTimeout(() => setNeedPayMsg(""), 1200);
    } catch (e) {
      console.error("copy url error:", e);
      setError(t("songDetails.copyError"));
    }
  };

  const handleCopyLyrics = async () => {
    try {
      await navigator.clipboard.writeText(song?.letra || "");
      setNeedPayMsg(t("songDetails.copiedLyrics"));
      setTimeout(() => setNeedPayMsg(""), 1200);
    } catch (e) {
      console.error("copy lyrics error:", e);
      setError(t("songDetails.copyError"));
    }
  };

  const handleShare = async (url, label) => {
    if (!paid) return showNeedPay();
    if (!url) return;

    try {
      if (navigator.share) {
        await navigator.share({
          title: song?.titulo || "Song",
          text: `${song?.titulo || "Song"} - ${label}`,
          url
        });
      } else {
        await navigator.clipboard.writeText(url);
        setNeedPayMsg(t("songDetails.copied"));
        setTimeout(() => setNeedPayMsg(""), 1200);
      }
    } catch (e) {
      // share can fail if user cancels, no problema
      console.log("share cancelled/failed", e);
    }
  };

  const handleDownload = (url) => {
    if (!paid) return showNeedPay();
    if (!url) return;

    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noreferrer";
    a.download = `${(song?.titulo || "song").slice(0, 40)}.mp3`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handlePayStripe = () => {
    navigate(`/app/pagos?songId=${songId}`, { replace: false });
  };

  if (!firebaseUser && !loading) {
    return (
      <div className="page">
        <div className="container">
          <div className="card">
            <h1 className="h1">{t("auth.needLoginTitle")}</h1>
            <p className="p-muted" style={{ marginTop: 8 }}>
              {t("auth.needLoginDesc")}
            </p>
            <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={() => navigate("/login")}>
              {t("auth.goLogin")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page">
        <div className="container">
          <div className="card">
            <div className="profile-loading" style={{ marginTop: 0 }}>
              <div className="spinner" />
              <p>{t("songDetails.loading")}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!song) {
    return (
      <div className="page">
        <div className="container">
          <div className="card">
            <div className="alert-error">{error || t("songDetails.notFound")}</div>
            <button className="btn" style={{ marginTop: 12 }} onClick={() => navigate("/app/songs")}>
              {t("songDetails.backToSongs")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const titulo = (song.titulo || "").trim() || t("songDetails.untitled");
  const estilo = (song.estilo || "").trim() || "—";
  const prompt = (song.prompt || "").trim() || "—";
  const status = statusLabel(song.status);

  const renderPlayer = (label, versionKey, url) => {
    const active = playingKey === versionKey && isPlaying;

    return (
      <div className={`sd-player ${active ? "active" : ""}`}>
        <div className="sd-player-left">
          <button
            type="button"
            className={`sd-play ${active ? "playing" : ""}`}
            onClick={() => handleTogglePlay(versionKey, url)}
            disabled={!url}
            title={url ? t("songDetails.play") : t("songDetails.notAvailable")}
          >
            {active ? "⏸" : "▶"}
          </button>

          <div className="sd-player-meta">
            <div className="sd-player-title">
              {label} {active ? <span className="sd-now">{t("songDetails.nowPlaying")}</span> : null}
            </div>
            <div className="sd-player-sub">{url ? t("songDetails.audioReady") : t("songDetails.audioMissing")}</div>
          </div>
        </div>

        <div className="sd-player-actions">
          <button className="btn sd-mini" type="button" onClick={() => handleDownload(url)}>
            {t("songDetails.download")}
          </button>
          <button className="btn sd-mini" type="button" onClick={() => handleShare(url, label)}>
            {t("songDetails.share")}
          </button>
          <button className="btn sd-mini" type="button" onClick={() => handleCopyUrl(url)}>
            {t("songDetails.copyUrl")}
          </button>
          
        </div>
      </div>
    );
  };

  return (
    <div className="page">
      <div className="container">
        {/* Top bar */}
        <div className="sd-top">
          <button className="btn" onClick={() => navigate("/app/songs")}>
            {t("songDetails.back")}
          </button>

          <div className="sd-top-right">
            <span className={`sd-status status-${String(song.status || "").toLowerCase()}`}>{status}</span>
            <span className={`sd-paid ${paid ? "yes" : "no"}`}>
              {paid ? t("songDetails.paidYes") : t("songDetails.paidNo")}
            </span>
          </div>
        </div>

        {/* Header */}
        <div className="sd-header card">
          <div className="sd-header-left">
            <div className="sd-icon" aria-hidden>🎵</div>

            <div className="sd-htext">
              {/* ✅ Título + botón editar */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                {!editandoTitulo ? (
                  <h1 className="h1" style={{ margin: 0 }}>{tituloMostrado}</h1>
                ) : (
                  <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 10 }}>
                    <input
                      className="input"
                      value={nuevoTitulo}
                      onChange={(e) => setNuevoTitulo(e.target.value)}
                      placeholder={t("song.noTitle")}
                      maxLength={80}
                      style={{ flex: 1 }}
                    />
                    <button
                      className="btn btn-primary"
                      onClick={onGuardarNuevoTitulo}
                      disabled={savingTitle}
                    >
                      {savingTitle ? t("common.saving") : t("common.save")}
                    </button>
                    <button
                      className="btn"
                      onClick={onCancelarEditarTitulo}
                      disabled={savingTitle}
                    >
                      {t("common.cancel")}
                    </button>
                  </div>
                )}

                {!editandoTitulo ? (
                  <button className="btn" onClick={onClickEditarTitulo}>
                    {t("songDetails.editTitle")}
                  </button>
                ) : null}
              </div>

              {msgTitle ? (
                <div className="p-muted" style={{ marginTop: 6 }}>
                  {msgTitle}
                </div>
              ) : null}

              {/* ✅ TUS SPAN QUEDAN IGUAL */}
              <div className="sd-chips">
                <span className="metaChip">
                  {t("songDetails.style")}: <b className="metaChipValue">{estilo}</b>
                </span>
                <span className="metaChip">
                  {t("songDetails.createdAt")}: <b className="metaChipValue">{formatDate(song.fechaCreacion)}</b>
                </span>
                <span className="metaChip">
                  {t("songDetails.songId")}: <b className="metaChipValue sd-id">{song.id}</b>
                </span>
              </div>
            </div>
          </div>


          <div className="sd-prompt">
            <div className="sd-label">{t("songDetails.promptUsed")}</div>
            <div className="sd-promptbox">{prompt}</div>
          </div>

          {needPayMsg ? <div className="sd-inlineMsg">{needPayMsg}</div> : null}
          {error ? <div className="alert-error" style={{ marginTop: 10 }}>{error}</div> : null}
        </div>

        {/* Players */}
        <div className="card">
          <h2 className="h2">{t("songDetails.playersTitle")}</h2>
          <p className="p-muted">{t("songDetails.playersDesc")}</p>

          <div className="sd-players">
            {renderPlayer("V1", "v1", v1Url)}
            {renderPlayer("V2", "v2", v2Url)}
          </div>

          {!paid ? (
            <div className="sd-payGate">
              <div className="sd-payTitle">{t("songDetails.payGateTitle")}</div>
              <div className="sd-payDesc">
                {t("songDetails.payGateDesc")}
              </div>

              <div className="sd-priceRow">
                <span className="sd-priceLabel">{t("songDetails.price")}</span>
                <span className="sd-priceValue">
                  {priceLabel ? `${priceLabel}` : t("songDetails.priceUnknown")}
                </span>
              </div>

              <button className="btn btn-primary" onClick={handlePayStripe}>
                {t("songDetails.payButton")}
              </button>

              <p className="note" style={{ marginTop: 10 }}>
                {t("pay.noteDesc")}
              </p>
            </div>
          ) : (
            <p className="note" style={{ marginTop: 10 }}>
              {t("songDetails.paidNote")}
            </p>
          )}
        </div>

        {/* Lyrics */}
        <div className="card" style={{ marginTop: 12 }}>
          <div className="sd-lyricsTop">
            <h2 className="h2" style={{ margin: 0 }}>{t("songDetails.lyricsTitle")}</h2>
            <button className="btn" onClick={handleCopyLyrics} type="button">
              {t("songDetails.copyLyrics")}
            </button>
          </div>

          <pre className="sd-lyricsBox">
            {(song.letra || "").trim() ? song.letra : t("songDetails.noLyrics")}
          </pre>
        </div>
      </div>
    </div>
  );
}
